// Shorten verbose aircraft model strings for tight UI surfaces.
// Examples:
//   "AS-350 B3 ASTAR"           -> "AS-350"
//   "BELL 407"                  -> "Bell 407"
//   "Robinson R44 II"           -> "Robinson R44"
//   "Sikorsky S-76C++"          -> "Sikorsky S-76"
//
// Pure function. Returns null/undefined unchanged.

const TRIM_TOKENS = new Set([
  'ASTAR', 'II', 'III', 'IV', 'PLUS',
]);

/**
 * @param {string | null | undefined} input
 * @returns {string | null | undefined}
 */
export function shortenAircraftType(input) {
  if (input == null) return input;
  const trimmed = String(input).trim();
  if (trimmed.length === 0) return trimmed;

  // Split on whitespace, then strip trailing "+" / "++" plus-marks from each
  // token (e.g. "S-76C++" -> "S-76C"). Drop standalone trim-token suffixes
  // (ASTAR / II / III / Plus / etc.).
  const parts = trimmed
    .split(/\s+/)
    .map((tok) => tok.replace(/\++$/, ''))
    .filter(Boolean);
  while (parts.length > 1) {
    const last = parts[parts.length - 1].toUpperCase();
    if (TRIM_TOKENS.has(last)) {
      parts.pop();
      continue;
    }
    break;
  }

  // Cap to 3 tokens — longer model strings stop being scannable in a list row.
  const capped = parts.slice(0, 3);

  return capped
    .map((tok) => {
      // Preserve hyphenated codes like "AS-350" / "S-76".
      if (/^[A-Za-z]+-?\d/.test(tok)) return tok.toUpperCase();
      // Mixed-case tokens (e.g. "GXi", "Bi") are intentional — leave them alone.
      const isAllUpper = tok === tok.toUpperCase();
      const isAllLower = tok === tok.toLowerCase();
      if (!isAllUpper && !isAllLower) return tok;
      // Preserve all-letter manufacturer abbreviations (BELL, MD).
      if (isAllUpper && /^[A-Z]{2,4}$/.test(tok)) {
        return tok.charAt(0) + tok.slice(1).toLowerCase();
      }
      // Title case otherwise.
      return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();
    })
    .join(' ');
}

export default shortenAircraftType;
